/* What the eligibility-stage screens (S1–S6) share: the trip as it stands and the guard
   that sends a deep link or a reload back to the first missing step. */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { nextStep, primaryLeg } from '@delaybuddy/contract';
import { resolveContext, useGoStep } from '../../app/navigation';
import type { ScreenId } from '../../app/registry';
import { useEnquiry, type Enquiry, type Trip } from '../../lib/api';
import {
  draftEnquiry,
  effectiveTrip,
  navContext,
  tripDraft,
  useEnquiryId,
  useSlot,
  type TripDraft,
} from '../../lib/funnel';

/** The enquiry with the effective trip: what the resolver should see on S1/S2. */
function effectiveEnquiry(enquiry: Enquiry | undefined, draft: TripDraft): Enquiry {
  const trip = effectiveTrip(enquiry, draft);
  if (enquiry && enquiry.trip.date) return enquiry;
  if (enquiry) return { ...enquiry, trip };
  return draftEnquiry({ ...draft, ...trip });
}

/* The order of the eligibility stage as the resolver walks it (S5 comes before S4). */
const RANK: Record<string, number> = {
  S1: 0,
  S2: 1,
  S3: 2,
  S5: 3,
  S4: 4,
  S6: 5,
  'S6-stop': 5,
};
const rank = (step: string, leg = 0) => (RANK[step] ?? 99) * 10 + Math.min(leg, 9);

export interface StageState {
  enquiryId: string | null;
  enquiry: Enquiry | undefined;
  /** The trip as the screen should show it (`effectiveTrip` in lib/funnel). */
  trip: Trip;
  isLoading: boolean;
  error: unknown;
  /** Stop guarding: the screen is leaving on its own (e.g. starting a new check). */
  hold: () => void;
}

/**
 * Loads the enquiry (if any) and, when the resolver says a step before this one is missing
 * (a deep link, a reload, the back button after an edit reset the answers), replaces the
 * URL with that step. Never pushes forward: going back to look at an answered step is fine.
 */
export function useStage(screen: ScreenId, leg = 0): StageState {
  const enquiryId = useEnquiryId();
  const query = useEnquiry(enquiryId);
  const draft = useSlot(tripDraft);
  const nav = useSlot(navContext);
  const goStep = useGoStep();
  const enquiry = query.data;
  const waiting = !!enquiryId && !enquiry;

  const next = useMemo(
    () => (waiting ? null : nextStep(effectiveEnquiry(enquiry, draft), resolveContext(nav))),
    [waiting, enquiry, draft, nav],
  );
  const behind = !!next && rank(next.step, next.leg ?? 0) < rank(screen, leg);
  const held = useRef(false);
  useEffect(() => {
    if (behind && next && !held.current) goStep(next, enquiryId ?? '', { replace: true });
  }, [behind, next, enquiryId, goStep]);
  const hold = useCallback(() => {
    held.current = true;
  }, []);

  return {
    enquiryId,
    enquiry,
    trip: effectiveTrip(enquiry, draft),
    isLoading: waiting && query.isLoading,
    error: query.error,
    hold,
  };
}

/** The flight S4–S6 are about (`primaryLeg`), by ident. */
export function primaryIdent(enquiry: Enquiry | undefined): string | null {
  if (!enquiry) return null;
  const leg = primaryLeg(enquiry);
  return leg?.flight?.operatingIdent ?? leg?.manualIdent ?? null;
}
